import type { ReactiveController, ReactiveControllerHost } from "lit";
import { html, nothing } from "lit";
import { SUBAGENT_HINTS } from "../control-hints.ts";
import { icons } from "../icons.ts";
import type { AgentRunEventSummary, ChatSubagentCard } from "../ui-types.ts";
import { renderCollapsibleMarkdownPrompt } from "./collapsible-markdown-prompt.ts";
import { subagentRunAgentLine, subagentRunTitle } from "./subagent-run-display.ts";
import {
  isSubagentRunCancellable as isRunCancellable,
  isSubagentRunTerminal as isRunTerminal,
} from "./subagent-run-status.ts";

// ---------------------------------------------------------------------------
// Typewriter animation configuration
// ---------------------------------------------------------------------------
const TYPEWRITER_MS_PER_CHAR = 15; // ~66 chars/sec — feels natural, not sluggish
const TYPEWRITER_MIN_BATCH = 3; // reveal at least N chars per frame for speed
const TYPEWRITER_MAX_BATCH = 8; // cap per-frame reveal so very fast streams still animate

// ---------------------------------------------------------------------------
// Typewriter controller — drives character-by-character reveal via RAF
// ---------------------------------------------------------------------------
class TypewriterController implements ReactiveController {
  private _host: ReactiveControllerHost;
  private _rafId: number | null = null;
  private _startIndex: number = 0;
  private _lastFrameTime: number = 0;

  constructor(host: ReactiveControllerHost) {
    this._host = host;
    host.addController(this);
  }

  /** Start (or restart) the typewriter animation for the given streamText. */
  start(fullText: string, startIndex: number, onUpdate: (index: number) => void) {
    this._cancel();
    this._startIndex = startIndex;
    this._lastFrameTime = 0;

    const tick = (rafTime: number) => {
      const text = fullText;
      if (!text || this._startIndex >= text.length) {
        onUpdate(text?.length ?? this._startIndex);
        this._rafId = null;
        return;
      }

      const elapsed = this._lastFrameTime ? rafTime - this._lastFrameTime : 0;
      this._lastFrameTime = rafTime;

      // Compute how many characters to reveal this frame
      const charsElapsed = Math.floor(elapsed / TYPEWRITER_MS_PER_CHAR);
      const batchSize = Math.max(
        TYPEWRITER_MIN_BATCH,
        Math.min(TYPEWRITER_MAX_BATCH, charsElapsed || TYPEWRITER_MIN_BATCH),
      );

      this._startIndex = Math.min(this._startIndex + batchSize, text.length);
      onUpdate(this._startIndex);

      if (this._startIndex < text.length) {
        this._rafId = requestAnimationFrame(tick);
      } else {
        this._rafId = null;
      }
    };

    this._rafId = requestAnimationFrame(tick);
  }

  /** Immediately cancel any running animation. */
  stop() {
    this._cancel();
  }

  private _cancel() {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  /** Lit controller lifecycle: clean up on host disconnect. */
  hostDisconnected() {
    this._cancel();
  }
}

function statusLabel(s: ChatSubagentCard["status"]): string {
  switch (s) {
    case "queued":
      return "Queued";
    case "spawning":
      return "Starting…";
    case "accepted":
      return "Accepted";
    case "cold-start":
      return "Cold start…";
    case "running":
      return "Running";
    case "done":
      return "Done";
    case "error":
      return "Error";
    case "cancelled":
      return "Cancelled";
    default:
      return s;
  }
}

function phaseLabel(phase?: string): string {
  if (!phase) return "";
  switch (phase) {
    case "thinking":
      return "Thinking";
    case "compacting":
      return "Compacting";
    case "start":
      return "Started";
    case "end":
      return "Finished";
    case "error":
      return "Error";
    default:
      return phase;
  }
}

function formatAge(ts: number): string {
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  return `${Math.floor(hrs / 24)}d`;
}

function renderCollapsedCard(c: ChatSubagentCard, now: number): ReturnType<typeof html> {
  return html`
    <div class="chat-subagent-row__collapsed">
      <div class="chat-subagent-row__collapsed-left">
        <span class="chat-subagent-row__status chat-subagent-row__status--${c.status}">
          ${statusLabel(c.status)}
        </span>
        <span class="chat-subagent-row__kind muted">Subagent</span>
        <span class="chat-subagent-row__label">${subagentRunTitle(c)}</span>
        ${c.phase ? html`<span class="chat-subagent-row__phase muted">${phaseLabel(c.phase)}</span>` : nothing}
      </div>
      <div class="chat-subagent-row__collapsed-right muted">
        ${c.updatedAt ? html`<span class="chat-subagent-row__age">${formatAge(c.updatedAt)}</span>` : nothing}
        ${c.toolHint ? html`<span class="chat-subagent-row__tool-hint">${c.toolHint}</span>` : nothing}
        ${
          c.outputPreview
            ? html`<span class="chat-subagent-row__preview">${c.outputPreview.slice(-120)}</span>`
            : nothing
        }
      </div>
    </div>
  `;
}

// JAISIU-3335 / UX-R11: in-card event timeline removed. The AgentRunEventSummary
// stream on ChatSubagentCard is still populated by app-gateway from the
// eventLogBySession ring; users reach it via the Inspector's Run log tab (Pillar E).
// ---------------------------------------------------------------------------
// Typewriter rendering helpers
// ---------------------------------------------------------------------------
const STREAM_DISPLAY_LIMIT = 4000; // max chars shown (matches old behaviour)

function renderTypewriterText(c: ChatSubagentCard): ReturnType<typeof html> {
  const fullText = c.streamText ?? "";
  const displayText = fullText.slice(-STREAM_DISPLAY_LIMIT);
  const offset = Math.max(0, fullText.length - STREAM_DISPLAY_LIMIT); // absolute start in fullText

  const revealed = c.typewriterIndex ?? displayText.length;
  const absoluteRevealed = offset + revealed;

  // Only animate if typewriter hasn't finished
  const isAnimating = c.typewriterActive && revealed < displayText.length;
  const revealedSlice = isAnimating ? displayText.slice(0, revealed) : displayText;

  const tokenLabel =
    fullText.length > 0
      ? html`<span class="typewriter-token-count" title="Typewriter reveal progress">
          ${absoluteRevealed.toLocaleString()} / ${fullText.length.toLocaleString()} chars
        </span>`
      : nothing;

  return html`
    ${
      isAnimating
        ? html`${revealedSlice}<span class="typewriter-cursor" aria-hidden="true">▌</span
            >${tokenLabel}`
        : html`${revealedSlice}${tokenLabel}`
    }
  `;
}

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------
export type SubagentRunRowProps = {
  card: ChatSubagentCard;
  /**
   * Explicit per-id expansion state from `chatSubagentExpanded[id]`. Pass
   * `undefined` when no user-driven toggle has been recorded for this row.
   * The component combines this with `card.status` and `defaultExpanded`
   * to derive the effective visibility — see `resolveRowExpanded`.
   */
  expanded: boolean | undefined;
  /**
   * JAISIU-XXXX — when the caller knows the row should default to expanded
   * (e.g. a freshly-spawned live run), pass `true`. Ignored once the run
   * becomes terminal: a finished run only expands when the operator has
   * explicitly toggled it. This keeps long lists of completed runs from
   * overshadowing the chat.
   */
  defaultExpanded?: boolean;
  /** When true, a terminal run row renders expanded (operator peek). */
  terminalOpen?: boolean;
  onToggle: () => void;
  onOpenSessionTab?: () => void;
  /**
   * Parent → children hierarchy: when the caller has resolved that this
   * card's `parentSessionKey` points at another in-scope card, the
   * parent's task / label + short session id are passed here so the
   * in-thread row can surface the spawning relationship. The inspector
   * uses the same data on the Subagents tab; here it lives just under
   * the meta line as a small muted badge.
   */
  parentLabel?: string | null;
  parentShortId?: string | null;
  /**
   * True when this card is a child of the currently-selected parent
   * (Inspector → Subagents → "Focus children"). Drives a subtle tinted
   * box-shadow on the row so the highlighted children are easy to spot
   * without leaving the chat.
   */
  isHighlightedChild?: boolean;
  /**
   * JAISIU-3319 / Pillar H: ask the gateway to cancel the underlying run.
   * Disabled once the run has already settled (`done`/`error`/`cancelled`).
   */
  onCancel?: () => void;
  /**
   * JAISIU-3319 / Pillar H: respawn the same task with the same agent.
   * Disabled while the original run is still live.
   */
  onRetry?: () => void;
  /** True while the cancel RPC is in flight; disables the button to avoid double-submits. */
  cancelling?: boolean;
  /** True while the retry spawn RPC is in flight. */
  retrying?: boolean;
};

/** True while the run can still be cancelled (still queued/spawning/running). */
export function isSubagentRunCancellable(card: ChatSubagentCard): boolean {
  return isRunCancellable(card.status);
}

/** True once the run has settled (done / error / cancelled). */
export function isSubagentRunTerminal(card: ChatSubagentCard): boolean {
  return isRunTerminal(card.status);
}

/**
 * JAISIU-XXXX — derive the effective expansion for a subagent run row.
 *
 * Rules:
 * - Terminal runs (`done`/`error`/`cancelled`) render collapsed unless
 *   the operator has explicitly opened them in this session
 *   (`terminalOpen === true`). This keeps long finished lists from
 *   overshadowing the chat on reload.
 * - Live runs (`queued`/`spawning`/`accepted`/`cold-start`/`running`)
 *   honor the explicit toggle, and otherwise default to `defaultExpanded`
 *   — which the caller passes `true` for a freshly-spawned run we want
 *   visible.
 */
export function resolveRowExpanded(
  card: ChatSubagentCard,
  explicit: boolean | undefined,
  defaultExpanded: boolean | undefined,
  terminalOpen?: boolean,
): boolean {
  if (isSubagentRunTerminal(card)) {
    return terminalOpen === true;
  }
  if (explicit === true) return true;
  if (explicit === false) return false;
  return Boolean(defaultExpanded);
}

export function renderSubagentRunRow(props: SubagentRunRowProps) {
  const c = props.card;
  const expanded = resolveRowExpanded(c, props.expanded, props.defaultExpanded, props.terminalOpen);
  const now = Date.now();
  const isTerminal = isSubagentRunTerminal(c);
  const canCancel = isSubagentRunCancellable(c);
  const canRetry = Boolean(props.onRetry) && isTerminal;

  return html`
    <div
      class="chat-subagent-row ${expanded ? "chat-subagent-row--expanded" : ""} ${isTerminal ? "chat-subagent-row--terminal" : ""} ${props.isHighlightedChild ? "chat-subagent-row--child-highlighted" : ""}"
      role="region"
      data-run-status=${c.status}
      data-parent-id=${props.parentShortId ?? ""}
      aria-label=${`Subagent run: ${c.label}`}
    >
      <button
        type="button"
        class="chat-subagent-row__header"
        @click=${props.onToggle}
        aria-expanded=${expanded}
      >
        <span class="chat-subagent-row__chevron" aria-hidden="true">${expanded ? "▼" : "▶"}</span>
        ${!expanded ? renderCollapsedCard(c, now) : nothing}
        ${expanded ? html` <span class="chat-subagent-row__kind muted">Subagent</span> ` : nothing}
        ${expanded ? html`<span class="chat-subagent-row__label">${subagentRunTitle(c)}</span>` : nothing}
        ${expanded ? html`<span class="chat-subagent-row__status chat-subagent-row__status--${c.status}">${statusLabel(c.status)}</span>` : nothing}
      </button>
      ${
        expanded
          ? html`
              <div class="chat-subagent-row__meta muted">
                ${subagentRunAgentLine(c)}
                ${c.runId ? html`<span class="mono"> · ${c.runId.slice(0, 8)}…</span>` : nothing}
                ${c.createdAt ? html`<span> · ${formatAge(c.createdAt)} ago</span>` : nothing}
              </div>
              ${
                // Parent → children hierarchy: a small badge under the
                // meta line so the in-thread chat list shows the same
                // spawning relationship the Subagents inspector does.
                // Only renders when the caller resolved a parent in
                // scope (parentSessionKey matched another in-flight
                // card's childSessionKey).
                props.parentLabel || props.parentShortId
                  ? html`
                      <p
                        class="chat-subagent-row__parent mono"
                        title=${`Parent session: ${props.parentShortId ?? ""}`}
                      >
                        <span class="chat-subagent-row__parent-icon" aria-hidden="true">↳</span>
                        <span class="chat-subagent-row__parent-label">${props.parentLabel ?? "parent"}</span>
                        ${
                          props.parentShortId
                            ? html`<span class="chat-subagent-row__parent-id muted"
                                >${props.parentShortId}</span
                              >`
                            : nothing
                        }
                      </p>
                    `
                  : nothing
              }
              ${
                // Session files this subagent was given. Listing the same
                // ids the child's preparation node resolved is how the
                // operator answers "did it actually get the video?" without
                // reading the child's transcript.
                c.sessionFileIds && c.sessionFileIds.length > 0
                  ? html`
                      <div
                        class="chat-subagent-row__files"
                        data-testid="subagent-session-files"
                        title="Session files handed to this subagent"
                      >
                        <span class="muted">Files:</span>
                        ${c.sessionFileIds.map(
                          (id) => html`<code class="mono chat-subagent-row__file-id">${id}</code>`,
                        )}
                      </div>
                    `
                  : nothing
              }
              ${
                c.task?.trim()
                  ? html`<div class="chat-subagent-row__task">
                      ${renderCollapsibleMarkdownPrompt({
                        text: c.task,
                        title: "Prompt",
                      })}
                    </div>`
                  : nothing
              }
              ${nothing /* JAISIU-3335 / UX-R11: timeline hidden — see Inspector → Run log tab */}
              ${
                c.childSessionKey && props.onOpenSessionTab
                  ? html`
                      <button
                        type="button"
                        class="btn chat-subagent-row__open-tab"
                        title=${SUBAGENT_HINTS.openSessionTab(c.label)}
                        @click=${(e: Event) => {
                          e.stopPropagation();
                          props.onOpenSessionTab?.();
                        }}
                      >
                        Open session tab · full chat
                      </button>
                    `
                  : nothing
              }
              <div
                class="chat-subagent-row__controls"
                role="group"
                aria-label="Subagent run controls"
              >
                ${
                  props.onCancel
                    ? html`
                        <button
                          type="button"
                          class="btn chat-subagent-row__cancel"
                          data-testid="chat-subagent-row__cancel"
                          ?disabled=${!canCancel || props.cancelling}
                          title=${
                            canCancel
                              ? "Cancel this subagent run"
                              : "Already settled — nothing to cancel"
                          }
                          @click=${(e: Event) => {
                            e.stopPropagation();
                            if (canCancel && !props.cancelling) {
                              props.onCancel?.();
                            }
                          }}
                        >
                          ${props.cancelling ? "Cancelling…" : "Cancel"}
                        </button>
                      `
                    : nothing
                }
                ${
                  props.onRetry
                    ? html`
                        <button
                          type="button"
                          class="btn chat-subagent-row__retry"
                          data-testid="chat-subagent-row__retry"
                          ?disabled=${!canRetry || props.retrying}
                          title=${
                            canRetry
                              ? "Respawn this task with the same agent"
                              : "Wait for the run to settle before retrying"
                          }
                          @click=${(e: Event) => {
                            e.stopPropagation();
                            if (canRetry && !props.retrying) {
                              props.onRetry?.();
                            }
                          }}
                        >
                          ${props.retrying ? "Retrying…" : "Retry"}
                        </button>
                      `
                    : nothing
                }
              </div>
              ${c.error ? html`<div class="callout danger chat-subagent-row__err">${c.error}</div>` : nothing}
              ${
                c.streamText
                  ? html`
                      <div class="chat-subagent-row__stream mono muted" aria-live="polite">
                        ${renderTypewriterText(c)}
                      </div>
                    `
                  : nothing
              }
            `
          : nothing
      }
    </div>
  `;
}

export function renderSubagentRunsToolbar(props: {
  filter: "all" | "running";
  onFilterChange: (next: "all" | "running") => void;
  onCollapseAll: () => void;
  onJumpLatestRunning?: () => void;
  hasRunning: boolean;
}) {
  return html`
    <div class="chat-subagent-toolbar" role="toolbar" aria-label="Subagent runs">
      <span class="chat-subagent-toolbar__label muted">Subagent runs</span>
      <button
        type="button"
        class="btn ${props.filter === "all" ? "primary" : ""}"
        @click=${() => props.onFilterChange("all")}
      >
        All
      </button>
      <button
        type="button"
        class="btn ${props.filter === "running" ? "primary" : ""}"
        @click=${() => props.onFilterChange("running")}
      >
        Active
      </button>
      <button type="button" class="btn" @click=${props.onCollapseAll}>Collapse all</button>
      ${
        props.hasRunning && props.onJumpLatestRunning
          ? html`
              <button type="button" class="btn" @click=${props.onJumpLatestRunning}>
                Latest active ${icons.arrowDown}
              </button>
            `
          : nothing
      }
    </div>
  `;
}
